import AXIOS_INSTANCE from '@/src/api/axiosInstance'
import { useLab, usePatient } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { labOrderFormSchema, type LabOrderFormValues } from '@schema/labOrder.schema'
import { GeneralResMock } from '@type/general.type'
import { LabOrderCreatePayload } from '@type/lab.type'
import { LaboratoryResMock } from '@type/laboratory.type'
import { FormButton, Modal, SelectInputField, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { debounce } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import type { GroupBase } from 'react-select'

type Props = {
  onClose: () => void
  patientId?: string
  encounterId?: string
}

type LabTestOption = { label: string; value: number | string }
type PatientOption = { label: string; value: string }

type ExtendedLabOrderFormValues = LabOrderFormValues & { patient_id?: string }

async function searchLabTests(inputValue: string): Promise<LabTestOption[]> {
  const q = new URLSearchParams({
    'page[number]': '1',
    'page[size]': '50',
    sort_by: 'name',
    sort_dir: 'asc'
  })
  const trimmed = inputValue.trim()
  if (trimmed) q.set('query', trimmed)

  const { data } = await AXIOS_INSTANCE.get<GeneralResMock<LaboratoryResMock[]>>(`/lab_tests?${q.toString()}`)
  const rows = Array.isArray(data.data) ? data.data : []
  return rows.map(lab => ({
    label: lab.attributes.name,
    value: Number(lab.id)
  }))
}

// patient searching will use `usePatient` hook with a debounced query state below

function buildLabOrderPayload(
  patientId: string,
  lab_test_ids: number[],
  note: string,
  encounterId?: string,
  test_names: string[] = []
): LabOrderCreatePayload {
  const relationships: Record<string, any> = {
    patient: { data: { id: patientId, type: 'patient' } }
  }
  if (encounterId) {
    relationships.encounter = { data: { id: encounterId, type: 'encounter' } }
  }

  return {
    data: {
      type: 'lab_order',
      attributes: {
        lab_test_ids,
        note: note.trim(),
        ...(test_names.length > 0 ? { test_names } : {})
      },
      relationships
    }
  } as unknown as LabOrderCreatePayload
}

const AddLabOrder = ({ onClose, patientId: patientIdProp, encounterId: encounterIdProp }: Props) => {
  const { patientId: patientIdParam, encounterId: encounterIdParam } = useParams<{
    patientId: string
    encounterId: string
  }>()
  const resolvedPatientId = patientIdProp ?? patientIdParam
  const resolvedEncounterId = encounterIdProp ?? encounterIdParam
  const methods = useForm<ExtendedLabOrderFormValues>({
    defaultValues: { lab_tests: [], note: '' },
    resolver: yupResolver(labOrderFormSchema)
  })
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting }
  } = methods

  const { createLab } = useLab()

  const { singlePatient } = usePatient({ id: resolvedPatientId })

  // patient search state + debounced handler
  const [patientSearch, setPatientSearch] = useState('')
  const debouncedSetPatientSearch = useCallback(
    debounce((q: string) => setPatientSearch(q), 400),
    []
  )
  const handlePatientSearch = (q: string) => debouncedSetPatientSearch(q)

  const { patients } = usePatient({ query: patientSearch, page: 1, limit: 50 })

  // if modal was opened with an existing patient, populate default value
  useEffect(() => {
    if (singlePatient) {
      setValue('patient_id', String(singlePatient.id))
    } else if (resolvedPatientId) {
      setValue('patient_id', String(resolvedPatientId))
    }
  }, [singlePatient, resolvedPatientId, setValue])

  const loadLabTestOptions = useCallback((inputValue: string) => searchLabTests(inputValue), [])

  const onSubmit = async (form: ExtendedLabOrderFormValues) => {
    const finalPatientId = (watch('patient_id') as string | undefined) ?? resolvedPatientId
    if (!finalPatientId) {
      showToast('Missing patient')
      return
    }

    const lab_test_ids = form.lab_tests.filter(t => typeof t.value === 'number').map(t => t.value as number)
    const test_names = form.lab_tests.filter(t => typeof t.value === 'string').map(t => String(t.label).trim())
    const payload = buildLabOrderPayload(finalPatientId, lab_test_ids, form.note ?? '', resolvedEncounterId, test_names)

    try {
      await createLab(payload)
      showToast('Lab order placed successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Order lab test">
        <form
          className="flex w-full min-w-0 max-w-[530px] flex-col gap-4 overflow-auto"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-4 overflow-auto p-6">
            {!resolvedPatientId && (
              <SelectInputField
                dropProps={{ align: { left: 'left', right: 'right' }, height: '200px' }}
                label="Search patient"
                labelKey={({ attributes }) => `${attributes.first_name} ${attributes.last_name}`}
                name="patient_id"
                onSearch={handlePatientSearch}
                options={patients || []}
                placeholder="Search Patient"
                searchPlaceholder="eg John doe"
                valueKey={{ key: 'id', reduce: true }}
              />
            )}

            <Controller
              control={control}
              name="lab_tests"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select<LabTestOption, true, GroupBase<LabTestOption>>
                  borderRadius={'15px'}
                  defaultOptions
                  error={error?.message}
                  getNewOptionData={(inputValue, optionLabel) => ({
                    label: String(optionLabel),
                    value: String(inputValue).trim()
                  })}
                  getOptionLabel={o => o.label}
                  getOptionValue={o => String(o.value)}
                  isAsync
                  isCreatable
                  isMulti
                  label="Test"
                  loadOptions={loadLabTestOptions}
                  onChange={opts => onChange((opts as LabTestOption[] | null) ?? [])}
                  placeholder="Search and select tests"
                  value={value as LabTestOption[]}
                />
              )}
            />

            <TextAreaField label="Note to lab" name="note" placeholder="Enter Note" />
          </div>
          <FormButton isLoading={isSubmitting} onClose={onClose} submit="Order" />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default AddLabOrder
